import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { File } from "../models/File";
import { FileService } from "../services/FileService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { MatterService } from "../services/MatterService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface FileContextProps {
  file: File | null;
  files: File[];
  loading: Loadings;
  error: Errors;
  createFile: (
    file: globalThis.File, // Use globalThis.File to ensure compatibility with the browser's File API
    fileData: Omit<
      File,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter" | "updatedBy"
    >,
  ) => Promise<string>;
  updateFile: (
    fileId: string,
    fileData: Partial<Omit<File, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteFileById: (fileId: string) => Promise<void>;
  setSelectedFile: (fileId: string) => void;
}

const FileContext = createContext<FileContextProps | undefined>(undefined);

export const useFile = (): FileContextProps => {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error("useFile must be used within a FileProvider");
  }
  return context;
};

export const FileProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();
  const [files, setFiles] = useState<File[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<Loadings>({ list: true });
  const [error, setError] = useState<Errors>({});

  const fetchFiles = async () => {
    if (!user || !currentMatterId) return;
    try {
      const fetchedFiles = await MatterService.getFiles(currentMatterId);
      setFiles(fetchedFiles);
      setError((prev) => ({ ...prev, list: null }));
    } catch (err: any) {
      console.error("Error fetching files:", err);
      setError((prev) => ({ ...prev, list: err }));
    } finally {
      setLoading((prev) => ({ ...prev, list: false }));
    }
  };

  useSocketEventByIds("files:changed", ["*"], (updatedDocument: File) => {
    console.log("File updated:", updatedDocument);
    if (file?._id === updatedDocument._id) {
      setFile(updatedDocument);
    }
    fetchFiles();
  });

  useEffect(() => {
    setLoading((prev) => ({ ...prev, list: true }));
    fetchFiles();
  }, [user, currentMatterId]);

  const setSelectedFile = async (fileId: string) => {
    if (!currentMatterId) return;
    try {
      setLoading((prev) => ({ ...prev, details: true }));
      const selectedFile = await FileService.getFile(fileId);
      setFile(selectedFile);
      setError((prev) => ({ ...prev, details: null }));
    } catch (err: any) {
      console.error("Error fetching file details:", err);
      setError((prev) => ({ ...prev, details: err }));
    } finally {
      setLoading((prev) => ({ ...prev, details: false }));
    }
  };

  const createFile = async (
    file: globalThis.File,
    fileData: Omit<
      File,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter" | "updatedBy"
    >,
  ): Promise<string> => {
    const newFileId = await FileService.createFile(file, {
      ...fileData,
      matter: currentMatterId!,
    });
    return newFileId;
  };

  const updateFile = async (
    fileId: string,
    updatedData: Partial<Omit<File, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    await FileService.updateFile(fileId, updatedData);
  };

  const deleteFileById = async (fileId: string) => {
    if (!currentMatterId) return;
    await FileService.deleteFile(fileId);
    setFiles((prev) => prev.filter((f) => f._id !== fileId));
    setFile(null);
  };

  return (
    <FileContext.Provider
      value={{
        loading,
        error,
        file,
        files,
        createFile,
        updateFile,
        deleteFileById,
        setSelectedFile,
      }}
    >
      {children}
    </FileContext.Provider>
  );
};
